/**
 * PositionSummaryBar - Enhanced Position Summary Display
 *
 * Space-efficient summary bar showing detailed position metrics.
 * Integrates with SignalR for real-time position updates.
 * Matches institutional-grade styling of QuantXAnalyticsPanel.
 */

import React, { memo, useMemo } from 'react';
import { Box, Typography, Tooltip, Chip } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

/**
 * Format currency value with sign
 */
const formatPnL = (value) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    const absValue = Math.abs(numValue);
    const formatted = absValue >= 1000
        ? `$${(absValue / 1000).toFixed(1)}K`
        : `$${absValue.toFixed(2)}`;
    return numValue >= 0 ? `+${formatted}` : `-${formatted}`;
};

/**
 * Format lot size
 */
const formatLots = (value) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    return numValue.toFixed(2);
};

/**
 * Compact metric display
 */
const MetricCell = ({ label, value, valueColor = '#fff', subValue = null }) => (
    <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.15,
        minWidth: '55px',
        padding: '0 4px'
    }}>
        <Typography
            sx={{
                fontSize: '9px',
                color: '#6b7280',
                fontFamily: 'monospace',
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                lineHeight: 1
            }}
        >
            {label}
        </Typography>
        <Typography
            sx={{
                fontSize: '11px',
                color: valueColor,
                fontWeight: 700,
                fontFamily: 'monospace',
                lineHeight: 1.1
            }}
        >
            {value}
        </Typography>
        {subValue && (
            <Typography
                sx={{
                    fontSize: '8px',
                    color: '#9ca3af',
                    fontFamily: 'monospace',
                    lineHeight: 1
                }}
            >
                {subValue}
            </Typography>
        )}
    </Box>
);

/**
 * Divider component
 */
const Divider = () => (
    <Box sx={{ width: '1px', height: '28px', backgroundColor: '#374151', mx: 0.5 }} />
);

/**
 * PositionSummaryBar Component
 */
const PositionSummaryBar = memo(({
    positions = [],
    strategyId = null,
    showDetails = true
}) => {
    // Calculate detailed summary metrics
    const summary = useMemo(() => {
        const positionsArray = Array.isArray(positions) ? positions : [];

        const filteredPositions = strategyId
            ? positionsArray.filter(p => p.strategyId === strategyId)
            : positionsArray;

        const openCount = filteredPositions.length;

        // Separate buy and sell positions
        const buyPositions = filteredPositions.filter(p =>
            p.direction?.toUpperCase() === 'BUY' || p.type?.toUpperCase() === 'BUY'
        );
        const sellPositions = filteredPositions.filter(p =>
            p.direction?.toUpperCase() === 'SELL' || p.type?.toUpperCase() === 'SELL'
        );

        // Calculate buy metrics
        const buyCount = buyPositions.length;
        const buyVolume = buyPositions.reduce((sum, p) => sum + (parseFloat(p.volume || p.lots) || 0), 0);
        const buyPnL = buyPositions.reduce((sum, p) => sum + (parseFloat(p.unrealizedPnL || p.pnL) || 0), 0);

        // Calculate sell metrics
        const sellCount = sellPositions.length;
        const sellVolume = sellPositions.reduce((sum, p) => sum + (parseFloat(p.volume || p.lots) || 0), 0);
        const sellPnL = sellPositions.reduce((sum, p) => sum + (parseFloat(p.unrealizedPnL || p.pnL) || 0), 0);

        // Calculate totals
        const totalPnL = buyPnL + sellPnL;
        const totalLots = buyVolume + sellVolume;

        return {
            openCount,
            buyCount,
            buyVolume,
            buyPnL,
            sellCount,
            sellVolume,
            sellPnL,
            totalPnL,
            totalLots,
            isEmpty: openCount === 0
        };
    }, [positions, strategyId]);

    // Color helpers
    const pnlColor = (val) => val > 0 ? '#22c55e' : val < 0 ? '#ef4444' : '#9ca3af';

    if (summary.isEmpty && !showDetails) {
        return null;
    }

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '6px 10px',
                backgroundColor: '#1a1a1a',
                borderTop: '1px solid #374151',
                borderRadius: '0 0 4px 4px',
                gap: 0.5,
                minHeight: '38px'
            }}
        >
            {/* Left: Position Count with Icon */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AccountBalanceWalletIcon sx={{ fontSize: 11, color: '#6b7280' }} />
                <MetricCell
                    label="Open"
                    value={summary.isEmpty ? '0' : summary.openCount}
                    valueColor={summary.isEmpty ? '#6b7280' : '#fff'}
                />
            </Box>

            <Divider />

            {/* Buy Section */}
            <Tooltip title={`${summary.buyCount} Buy positions @ ${formatLots(summary.buyVolume)} lots`} arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                        icon={<TrendingUpIcon sx={{ fontSize: 10, color: '#22c55e !important' }} />}
                        label={summary.buyCount}
                        size="small"
                        sx={{
                            backgroundColor: 'rgba(34, 197, 94, 0.12)',
                            color: '#22c55e',
                            fontSize: '10px',
                            height: '18px',
                            minWidth: '36px',
                            '& .MuiChip-label': { px: 0.5 },
                            '& .MuiChip-icon': { ml: 0.25 }
                        }}
                    />
                    <MetricCell
                        label="Vol"
                        value={formatLots(summary.buyVolume)}
                        valueColor="#22c55e"
                    />
                    <MetricCell
                        label="P&L"
                        value={formatPnL(summary.buyPnL)}
                        valueColor={pnlColor(summary.buyPnL)}
                    />
                </Box>
            </Tooltip>

            <Divider />

            {/* Sell Section */}
            <Tooltip title={`${summary.sellCount} Sell positions @ ${formatLots(summary.sellVolume)} lots`} arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                        icon={<TrendingDownIcon sx={{ fontSize: 10, color: '#ef4444 !important' }} />}
                        label={summary.sellCount}
                        size="small"
                        sx={{
                            backgroundColor: 'rgba(239, 68, 68, 0.12)',
                            color: '#ef4444',
                            fontSize: '10px',
                            height: '18px',
                            minWidth: '36px',
                            '& .MuiChip-label': { px: 0.5 },
                            '& .MuiChip-icon': { ml: 0.25 }
                        }}
                    />
                    <MetricCell
                        label="Vol"
                        value={formatLots(summary.sellVolume)}
                        valueColor="#ef4444"
                    />
                    <MetricCell
                        label="P&L"
                        value={formatPnL(summary.sellPnL)}
                        valueColor={pnlColor(summary.sellPnL)}
                    />
                </Box>
            </Tooltip>

            <Divider />

            {/* Total Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <MetricCell
                    label="Net P&L"
                    value={formatPnL(summary.totalPnL)}
                    valueColor={pnlColor(summary.totalPnL)}
                />
                <MetricCell
                    label="Exposure"
                    value={`${formatLots(summary.totalLots)}`}
                    valueColor={summary.totalLots > 0 ? '#fbbf24' : '#6b7280'}
                    subValue="lots"
                />
            </Box>
        </Box>
    );
});

PositionSummaryBar.displayName = 'PositionSummaryBar';

export default PositionSummaryBar;
